// Shared facade grouping for the booking surfaces (HomePage search + OrgBookingPage direct link).
// Kept in one place so the two pages can never drift out of sync (which is how facades first
// shipped to only one of them).

/**
 * Split categories into facade groups + ungrouped cards.
 *
 * @param {Array} categories  the org's (active) categories
 * @param {Array} facades     facade objects, each with `category_ids` (and maybe `status`)
 * @param {Object} opts
 *   - activeOnly:     skip facades whose status isn't 'active' (public list endpoint returns all)
 *   - onlyFacadeSlug: show just this one facade (facade deep-link); no ungrouped cards
 * @returns {{ facadeGroups: Array<{facade, childCats}>, ungroupedCats: Array }}
 */
export function groupByFacade(categories, facades, { activeOnly = false, onlyFacadeSlug = null } = {}) {
  if (!facades || facades.length === 0) return { facadeGroups: [], ungroupedCats: categories };
  const byId = new Map(categories.map((c) => [c.id, c]));
  const grouped = new Set();
  const groups = [];
  facades.forEach((f) => {
    if (activeOnly && f.status && f.status !== 'active') return;
    if (onlyFacadeSlug != null && String(f.slug) !== String(onlyFacadeSlug)) return;
    const kids = (f.category_ids || []).map((id) => byId.get(id)).filter(Boolean);
    kids.forEach((c) => grouped.add(c.id));
    if (kids.length) groups.push({ facade: f, childCats: kids });
  });
  // A facade deep-link shows only that facade; otherwise everything not in a facade shows flat.
  const ungroupedCats = onlyFacadeSlug != null ? [] : categories.filter((c) => !grouped.has(c.id));
  return { facadeGroups: groups, ungroupedCats };
}
